import React from "react";
import { Text, View } from "native-base";
import { ActivityIndicator, Alert, Platform, TextInput } from "react-native";
import { webFont } from "../../Universal/API/API";
import PressableWrapp from "../../Universal/Buttons/PressableReact/PressableWrapp";
import { PAYMENT_METHODS } from "../cartConstants";
import type { OrderDraft } from "../cartTypes";
import { formatCurrency } from "../helpers";
import {
  normalizePhoneInput,
  validateEmail,
  validateFullName,
  validatePhone,
} from "../cartValidation";

const webCursorPointer = Platform.OS === "web" ? ({ cursor: "pointer" } as any) : null;

const createInputStyle = ({
  scaleAll,
  multiline = false,
  compact = false,
  invalid = false,
}: {
  scaleAll: number;
  multiline?: boolean;
  compact?: boolean;
  invalid?: boolean;
}) =>
  ({
    width: "100%",
    minHeight: multiline
      ? compact
        ? 96 * scaleAll
        : 132 * scaleAll
      : compact
        ? 48 * scaleAll
        : 56 * scaleAll,
    borderRadius: compact ? 14 * scaleAll : 18 * scaleAll,
    borderWidth: 1,
    borderColor: invalid ? "#D93F21" : "#E6E1D8",
    backgroundColor: "#FFFFFF",
    paddingHorizontal: compact ? 12 * scaleAll : 16 * scaleAll,
    paddingVertical: multiline ? (compact ? 10 * scaleAll : 14 * scaleAll) : 0,
    color: "#111111",
    fontSize: compact ? 13 * scaleAll : 15 * scaleAll,
    fontFamily: webFont("Inter"),
    fontWeight: "500",
    outlineStyle: "none",
    textAlignVertical: multiline ? "top" : "center",
  } as any);

const FieldError: React.FC<{ text: string; scaleAll: number }> = ({ text, scaleAll }) => (
  <Text
    selectable={false}
    style={{
      fontFamily: webFont("Inter"),
      fontSize: 11 * scaleAll,
      color: "#D93F21",
      fontWeight: "600",
      marginTop: 6 * scaleAll,
    }}
  >
    {text}
  </Text>
);

const FieldLabel: React.FC<{
  text: string;
  required?: boolean;
  scaleAll: number;
  compact?: boolean;
}> = ({ text, required, scaleAll, compact = false }) => (
  <Text
    selectable={false}
    style={{
      fontFamily: webFont("Inter"),
      fontSize: compact ? 10 * scaleAll : 12 * scaleAll,
      color: "#6E665D",
      fontWeight: "700",
      marginBottom: compact ? 6 * scaleAll : 8 * scaleAll,
      textTransform: "uppercase",
      letterSpacing: 0.45,
    }}
  >
    {text}
    {required ? <Text style={{ color: "#D93F21" }}> *</Text> : null}
  </Text>
);

type CartCheckoutFooterProps = {
  isSmallVersion: boolean;
  scaleAll: number;
  isUserLoggedIn: boolean;
  orderDraft: OrderDraft;
  uniqueItemsCount: number;
  totalQuantity: number;
  totalPrice: number;
  onUpdateDraft: <K extends keyof OrderDraft>(field: K, value: OrderDraft[K]) => void;
  onClearCart: () => void;
  onOpenCatalog: () => void;
  onCreateCommercialOffer?: () => Promise<void> | void;
  onPrintCommercialOffer?: () => void;
  onSaveOrder?: () => Promise<void> | void;
  onSubmitOrder: () => Promise<void> | void;
};

const CartCheckoutFooter: React.FC<CartCheckoutFooterProps> = ({
  isSmallVersion,
  scaleAll,
  isUserLoggedIn,
  orderDraft,
  uniqueItemsCount,
  totalQuantity,
  totalPrice,
  onUpdateDraft,
  onClearCart,
  onOpenCatalog,
  onCreateCommercialOffer,
  onPrintCommercialOffer,
  onSaveOrder,
  onSubmitOrder,
}) => {
  const [touched, setTouched] = React.useState({
    phone: false,
    email: false,
    fullName: false,
  });
  const [isCreatingCommercialOffer, setIsCreatingCommercialOffer] = React.useState(false);
  const [isSavingOrder, setIsSavingOrder] = React.useState(false);
  const [isSubmittingOrder, setIsSubmittingOrder] = React.useState(false);

  const phoneError = React.useMemo(
    () => (touched.phone ? validatePhone(orderDraft.phone) : null),
    [orderDraft.phone, touched.phone]
  );
  const emailError = React.useMemo(
    () => (touched.email ? validateEmail(orderDraft.email) : null),
    [orderDraft.email, touched.email]
  );
  const fullNameError = React.useMemo(
    () => (touched.fullName ? validateFullName(orderDraft.fullName) : null),
    [orderDraft.fullName, touched.fullName]
  );

  const markTouched = React.useCallback((field: "phone" | "email" | "fullName") => {
    setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
  }, []);

  const ensureRequiredFieldsValid = React.useCallback(() => {
    const nextPhoneError = validatePhone(orderDraft.phone);
    const nextEmailError = validateEmail(orderDraft.email);
    const nextFullNameError = validateFullName(orderDraft.fullName);

    setTouched({
      phone: true,
      email: true,
      fullName: true,
    });

    if (nextPhoneError || nextEmailError || nextFullNameError) {
      Alert.alert(
        "Проверьте данные",
        nextPhoneError || nextEmailError || nextFullNameError || "Заполните обязательные поля"
      );
      return false;
    }

    return true;
  }, [orderDraft.email, orderDraft.fullName, orderDraft.phone]);

  const handleCreateCommercialOffer = React.useCallback(async () => {
    if (isCreatingCommercialOffer || isSavingOrder || isSubmittingOrder) {
      return;
    }

    if (!ensureRequiredFieldsValid()) {
      return;
    }

    try {
      setIsCreatingCommercialOffer(true);

      if (onCreateCommercialOffer) {
        await Promise.resolve(onCreateCommercialOffer());
        return;
      }

      Alert.alert("КП", "Формирование коммерческого предложения подключим на следующем шаге.");
    } finally {
      setIsCreatingCommercialOffer(false);
    }
  }, [
    ensureRequiredFieldsValid,
    isCreatingCommercialOffer,
    isSavingOrder,
    isSubmittingOrder,
    onCreateCommercialOffer,
  ]);

  const handleSaveOrder = React.useCallback(async () => {
    if (isSavingOrder || isCreatingCommercialOffer || isSubmittingOrder) {
      return;
    }

    if (!ensureRequiredFieldsValid()) {
      return;
    }

    try {
      setIsSavingOrder(true);

      if (onSaveOrder) {
        await Promise.resolve(onSaveOrder());
        return;
      }

      await Promise.resolve(onSubmitOrder());
    } finally {
      setIsSavingOrder(false);
    }
  }, [
    ensureRequiredFieldsValid,
    isCreatingCommercialOffer,
    isSavingOrder,
    isSubmittingOrder,
    onSaveOrder,
    onSubmitOrder,
  ]);

  const handleSubmitOrder = React.useCallback(async () => {
    if (isSubmittingOrder || isCreatingCommercialOffer || isSavingOrder) {
      return;
    }

    if (!ensureRequiredFieldsValid()) {
      return;
    }

    try {
      setIsSubmittingOrder(true);
      await Promise.resolve(onSubmitOrder());
    } finally {
      setIsSubmittingOrder(false);
    }
  }, [
    ensureRequiredFieldsValid,
    isCreatingCommercialOffer,
    isSavingOrder,
    isSubmittingOrder,
    onSubmitOrder,
  ]);

  const summaryCardMinHeight = 74 * scaleAll;

  return (
    <View
      style={{
        marginTop: 6 * scaleAll,
        marginBottom: 18 * scaleAll,
        borderRadius: 28 * scaleAll,
        backgroundColor: "#FFFFFF",
        borderWidth: 1,
        borderColor: "#ECE8E1",
        padding: 16 * scaleAll,
        shadowColor: "#000",
        shadowOpacity: 0.08,
        shadowRadius: 24,
        shadowOffset: { width: 0, height: 14 },
        elevation: 8,
      }}
    >
      <View style={{ marginBottom: 14 * scaleAll }}>
        <Text
          selectable={false}
          style={{
            fontFamily: webFont("Inter"),
            fontSize: 11 * scaleAll,
            color: "#7A746C",
            fontWeight: "700",
            textTransform: "uppercase",
            letterSpacing: 0.7,
            marginBottom: 10 * scaleAll,
          }}
        >
          Данные заказа
        </Text>

        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            marginHorizontal: -5 * scaleAll,
          }}
        >
          <View
            style={{
              width: isSmallVersion ? "100%" : undefined,
              flexGrow: 1,
              minWidth: isSmallVersion ? 0 : 180 * scaleAll,
              paddingHorizontal: 5 * scaleAll,
              marginBottom: 10 * scaleAll,
            }}
          >
            <FieldLabel text="Телефон" required={true} scaleAll={scaleAll} compact={true} />
            <TextInput
              value={orderDraft.phone}
              onChangeText={(value) => onUpdateDraft("phone", normalizePhoneInput(value))}
              onBlur={() => markTouched("phone")}
              placeholder="+7 (___) ___-__-__"
              placeholderTextColor="#A49C92"
              keyboardType="phone-pad"
              style={createInputStyle({
                scaleAll,
                compact: true,
                invalid: Boolean(phoneError),
              })}
            />
            {phoneError ? <FieldError text={phoneError} scaleAll={scaleAll} /> : null}
          </View>

          <View
            style={{
              width: isSmallVersion ? "100%" : undefined,
              flexGrow: 1,
              minWidth: isSmallVersion ? 0 : 210 * scaleAll,
              paddingHorizontal: 5 * scaleAll,
              marginBottom: 10 * scaleAll,
            }}
          >
            <FieldLabel text="Email" required={true} scaleAll={scaleAll} compact={true} />
            <TextInput
              value={orderDraft.email}
              onChangeText={(value) => onUpdateDraft("email", value)}
              onBlur={() => markTouched("email")}
              placeholder="mail@example.com"
              placeholderTextColor="#A49C92"
              keyboardType="email-address"
              autoCapitalize="none"
              style={createInputStyle({
                scaleAll,
                compact: true,
                invalid: Boolean(emailError),
              })}
            />
            {emailError ? <FieldError text={emailError} scaleAll={scaleAll} /> : null}
          </View>

          <View
            style={{
              width: isSmallVersion ? "100%" : undefined,
              flexGrow: 1,
              minWidth: isSmallVersion ? 0 : 170 * scaleAll,
              paddingHorizontal: 5 * scaleAll,
              marginBottom: 10 * scaleAll,
            }}
          >
            <FieldLabel text="ФИО" required={true} scaleAll={scaleAll} compact={true} />
            <TextInput
              value={orderDraft.fullName}
              onChangeText={(value) => onUpdateDraft("fullName", value)}
              onBlur={() => markTouched("fullName")}
              placeholder="Имя и фамилия"
              placeholderTextColor="#A49C92"
              style={createInputStyle({
                scaleAll,
                compact: true,
                invalid: Boolean(fullNameError),
              })}
            />
            {fullNameError ? <FieldError text={fullNameError} scaleAll={scaleAll} /> : null}
          </View>

          <View
            style={{
              width: isSmallVersion ? "100%" : undefined,
              flexGrow: 1,
              minWidth: isSmallVersion ? 0 : 190 * scaleAll,
              paddingHorizontal: 5 * scaleAll,
              marginBottom: 10 * scaleAll,
            }}
          >
            <FieldLabel text="Промокод" scaleAll={scaleAll} compact={true} />
            <TextInput
              value={orderDraft.promoCode}
              onChangeText={(value) => onUpdateDraft("promoCode", value)}
              placeholder="Если есть"
              placeholderTextColor="#A49C92"
              autoCapitalize="characters"
              style={createInputStyle({ scaleAll, compact: true })}
            />
          </View>

          <View
            style={{
              width: isSmallVersion ? "100%" : undefined,
              flexGrow: 1,
              minWidth: isSmallVersion ? 0 : 190 * scaleAll,
              paddingHorizontal: 5 * scaleAll,
              marginBottom: 10 * scaleAll,
            }}
          >
            <FieldLabel text="Имя заказа" scaleAll={scaleAll} compact={true} />
            <TextInput
              value={orderDraft.orderName}
              onChangeText={(value) => onUpdateDraft("orderName", value)}
              placeholder="Название"
              placeholderTextColor="#A49C92"
              style={createInputStyle({ scaleAll, compact: true })}
            />
          </View>
        </View>

        <View style={{ marginBottom: 10 * scaleAll }}>
          <FieldLabel text="Способ оплаты" scaleAll={scaleAll} compact={true} />
          <View
            style={{
              minHeight: 48 * scaleAll,
              borderRadius: 14 * scaleAll,
              borderWidth: 1,
              borderColor: "#E6E1D8",
              backgroundColor: "#FFFFFF",
              padding: 6 * scaleAll,
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 6 * scaleAll,
            }}
          >
            {PAYMENT_METHODS.map((item) => {
              const selected = orderDraft.paymentMethod === item.value;

              return (
                <PressableWrapp
                  key={item.value}
                  onPress={() => onUpdateDraft("paymentMethod", item.value)}
                  style={[
                    {
                      minHeight: 32 * scaleAll,
                      borderRadius: 12 * scaleAll,
                      paddingHorizontal: 10 * scaleAll,
                      paddingVertical: 6 * scaleAll,
                      backgroundColor: selected ? "#111111" : "#F7F5F0",
                      borderWidth: 1,
                      borderColor: selected ? "#111111" : "#E6E1D8",
                      justifyContent: "center",
                    },
                    webCursorPointer,
                  ]}
                >
                  <Text
                    selectable={false}
                    style={{
                      fontFamily: webFont("Inter"),
                      fontSize: 12 * scaleAll,
                      color: selected ? "#FFFFFF" : "#111111",
                      fontWeight: "600",
                    }}
                  >
                    {item.label}
                  </Text>
                </PressableWrapp>
              );
            })}
          </View>
        </View>

        <View>
          <FieldLabel text="Адрес, комментарий, ИНН" scaleAll={scaleAll} compact={true} />
          <TextInput
            value={orderDraft.addressCommentInn}
            onChangeText={(value) => onUpdateDraft("addressCommentInn", value)}
            placeholder="Адрес доставки или самовывоза, комментарии, реквизиты"
            placeholderTextColor="#A49C92"
            multiline={true}
            style={createInputStyle({ scaleAll, multiline: true, compact: true })}
          />
        </View>
      </View>

      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 14 * scaleAll,
          marginBottom: 12 * scaleAll,
        }}
      >
        <View
          style={{
            flexDirection: isSmallVersion ? "column" : "row",
            gap: 10 * scaleAll,
            flex: 1,
            minWidth: 0,
          }}
        >
            <View
              style={{
                minWidth: isSmallVersion ? 0 : 120 * scaleAll,
                minHeight: summaryCardMinHeight,
                borderRadius: 16 * scaleAll,
                backgroundColor: "#F7F5F0",
                borderWidth: 1,
                borderColor: "#E6E1D8",
                paddingHorizontal: 12 * scaleAll,
              paddingVertical: 10 * scaleAll,
              flex: isSmallVersion ? 0 : 1,
            }}
          >
            <Text
              selectable={false}
              style={{
                fontFamily: webFont("Inter"),
                fontSize: 10 * scaleAll,
                color: "#7A746C",
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: 0.6,
                marginBottom: 2 * scaleAll,
              }}
            >
              Позиции
            </Text>
            <Text
              selectable={false}
              style={{
                fontFamily: webFont("Inter"),
                fontSize: 18 * scaleAll,
                color: "#111111",
                fontWeight: "700",
              }}
            >
              {uniqueItemsCount}
            </Text>
          </View>

            <View
              style={{
                minWidth: isSmallVersion ? 0 : 140 * scaleAll,
                minHeight: summaryCardMinHeight,
                borderRadius: 16 * scaleAll,
                backgroundColor: "#F7F5F0",
                borderWidth: 1,
                borderColor: "#E6E1D8",
                paddingHorizontal: 12 * scaleAll,
              paddingVertical: 10 * scaleAll,
              flex: isSmallVersion ? 0 : 1,
            }}
          >
            <Text
              selectable={false}
              style={{
                fontFamily: webFont("Inter"),
                fontSize: 10 * scaleAll,
                color: "#7A746C",
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: 0.6,
                marginBottom: 2 * scaleAll,
              }}
            >
              Количество
            </Text>
            <Text
              selectable={false}
              style={{
                fontFamily: webFont("Inter"),
                fontSize: 18 * scaleAll,
                color: "#111111",
                fontWeight: "700",
              }}
            >
              {totalQuantity} шт.
            </Text>
          </View>
        </View>

        <View
          style={{
            minWidth: isSmallVersion ? "100%" : 200 * scaleAll,
            minHeight: summaryCardMinHeight,
            borderRadius: 18 * scaleAll,
            backgroundColor: "#111111",
            paddingHorizontal: 14 * scaleAll,
            paddingVertical: 12 * scaleAll,
            justifyContent: "center",
          }}
        >
          <Text
            selectable={false}
            style={{
              fontFamily: webFont("Inter"),
              fontSize: 11 * scaleAll,
              color: "rgba(255,255,255,0.7)",
              fontWeight: "700",
              textTransform: "uppercase",
              letterSpacing: 0.7,
              marginBottom: 4 * scaleAll,
            }}
          >
            Итого
          </Text>
          <Text
            selectable={false}
            style={{
              fontFamily: webFont("Inter"),
              fontSize: 22 * scaleAll,
              color: "#FFFFFF",
              fontWeight: "700",
            }}
          >
            {formatCurrency(totalPrice)} ₽
          </Text>
        </View>
      </View>

      <View
        style={{
          flexDirection: isSmallVersion ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isSmallVersion ? "stretch" : "center",
          gap: 10 * scaleAll,
        }}
      >
        <View
          style={{
            flexDirection: isSmallVersion ? "column" : "row",
            gap: 10 * scaleAll,
            alignItems: isSmallVersion ? "stretch" : "center",
          }}
        >
          <PressableWrapp
            onPress={onClearCart}
            style={[
              {
                minHeight: 50 * scaleAll,
                borderRadius: 18 * scaleAll,
                backgroundColor: "#F5F4F1",
                borderWidth: 1,
                borderColor: "#E6E1D8",
                paddingHorizontal: 18 * scaleAll,
                alignItems: "center",
                justifyContent: "center",
                minWidth: isSmallVersion ? undefined : 138 * scaleAll,
              },
              webCursorPointer,
            ]}
          >
            <Text
              selectable={false}
              style={{
                fontFamily: webFont("Inter"),
                fontSize: 13 * scaleAll,
                color: "#111111",
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Очистить
            </Text>
          </PressableWrapp>

          <PressableWrapp
            onPress={onOpenCatalog}
            style={[
              {
                minHeight: 50 * scaleAll,
                borderRadius: 18 * scaleAll,
                backgroundColor: "#FFFFFF",
                borderWidth: 1,
                borderColor: "#E6E1D8",
                paddingHorizontal: 18 * scaleAll,
                alignItems: "center",
                justifyContent: "center",
                minWidth: isSmallVersion ? undefined : 138 * scaleAll,
              },
              webCursorPointer,
            ]}
          >
            <Text
              selectable={false}
              style={{
                fontFamily: webFont("Inter"),
                fontSize: 13 * scaleAll,
                color: "#111111",
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              В каталог
            </Text>
          </PressableWrapp>
        </View>

        <View
          style={{
            flexDirection: isSmallVersion ? "column" : "row",
            gap: 10 * scaleAll,
            alignItems: isSmallVersion ? "stretch" : "center",
          }}
        >
          {isUserLoggedIn ? (
            <PressableWrapp
              onPress={handleSaveOrder}
              disabled={isSavingOrder || isCreatingCommercialOffer || isSubmittingOrder}
              style={[
                {
                  minHeight: 50 * scaleAll,
                  borderRadius: 18 * scaleAll,
                  backgroundColor: "#FFFFFF",
                  borderWidth: 1,
                  borderColor: "#E6E1D8",
                  paddingHorizontal: 18 * scaleAll,
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: isSmallVersion ? undefined : 148 * scaleAll,
                  opacity: isSavingOrder || isCreatingCommercialOffer || isSubmittingOrder ? 0.7 : 1,
                },
                webCursorPointer,
              ]}
            >
              {isSavingOrder ? (
                <ActivityIndicator color="#111111" size="small" />
              ) : (
                <Text
                  selectable={false}
                  style={{
                    fontFamily: webFont("Inter"),
                    fontSize: 13 * scaleAll,
                    color: "#111111",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Сохранить
                </Text>
              )}
            </PressableWrapp>
          ) : null}

          {true?//isUserLoggedIn ? 
          (
            <PressableWrapp
              onPress={handleCreateCommercialOffer}
              disabled={isCreatingCommercialOffer || isSavingOrder || isSubmittingOrder}
              style={[
                {
                  minHeight: 50 * scaleAll,
                  borderRadius: 18 * scaleAll,
                  backgroundColor: "#18A957",
                  paddingHorizontal: 18 * scaleAll,
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: isSmallVersion ? undefined : 92 * scaleAll,
                  opacity: isCreatingCommercialOffer || isSavingOrder || isSubmittingOrder ? 0.7 : 1,
                },
                webCursorPointer,
              ]}
            >
              {isCreatingCommercialOffer ? (
                <ActivityIndicator color="#FFFFFF" size="small" />
              ) : (
                <Text
                  selectable={false}
                  style={{
                    fontFamily: webFont("Inter"),
                    fontSize: 13 * scaleAll,
                    color: "#FFFFFF",
                    fontWeight: "800",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  КП
                </Text>
              )}
            </PressableWrapp>
          ) : null}

          <PressableWrapp
            onPress={onPrintCommercialOffer}
            disabled={!onPrintCommercialOffer}
            style={[
              {
                minHeight: 50 * scaleAll,
                borderRadius: 18 * scaleAll,
                backgroundColor: "#FFFFFF",
                borderWidth: 1,
                borderColor: "#111111",
                paddingHorizontal: 18 * scaleAll,
                alignItems: "center",
                justifyContent: "center",
                minWidth: isSmallVersion ? undefined : 126 * scaleAll,
                opacity: onPrintCommercialOffer ? 1 : 0.5,
              },
              webCursorPointer,
            ]}
          >
            <Text
              selectable={false}
              style={{
                fontFamily: webFont("Inter"),
                fontSize: 13 * scaleAll,
                color: "#111111",
                fontWeight: "800",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Печать КП
            </Text>
          </PressableWrapp>

          <PressableWrapp
            onPress={handleSubmitOrder}
            disabled={isSubmittingOrder || isCreatingCommercialOffer || isSavingOrder}
            style={[
              {
                minHeight: 50 * scaleAll,
                borderRadius: 18 * scaleAll,
                backgroundColor: "#111111",
                paddingHorizontal: 18 * scaleAll,
                alignItems: "center",
                justifyContent: "center",
                minWidth: isSmallVersion ? undefined : 142 * scaleAll,
                opacity: isSubmittingOrder || isCreatingCommercialOffer || isSavingOrder ? 0.7 : 1,
              },
              webCursorPointer,
            ]}
          >
            {isSubmittingOrder ? (
              <ActivityIndicator color="#FFFFFF" size="small" />
            ) : (
              <Text
                selectable={false}
                style={{
                  fontFamily: webFont("Inter"),
                  fontSize: 13 * scaleAll,
                  color: "#FFFFFF",
                  fontWeight: "700",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Заказать
              </Text>
            )}
          </PressableWrapp>
        </View>
      </View>
    </View>
  );
};

export default React.memo(CartCheckoutFooter);
